import { CardShell, FieldShell } from '@atlas/ui-components'
import { Button, Combobox, type ComboboxOption, Input, Textarea } from '@atlas/ui-primitives'
import type { FormEvent } from 'react'
import { ClassifyPicker } from '@/components/classification/ClassifyPicker'
import { MESSAGE_STATUS_LABEL, MESSAGE_TYPE_LABEL } from '@/domain/message.types'
import type { RailAxis } from '@/hooks/useLibrary'
import { EnumField } from './field'
import type { MessageFormState } from './form-model'

/** The new-row surface, owned by the container hook so the matrix itself stays prop-driven. */
export interface MatrixAddRowController {
	isOpen: boolean
	open: () => void
	cancel: () => void
	draft: MessageFormState
	set: <K extends keyof MessageFormState>(key: K, value: MessageFormState[K]) => void
	/** Registry axes behind the classification picker. */
	axes: RailAxis[]
	/** Every locale-axis value, offered as the new row's source locale. */
	localeOptions: ComboboxOption[]
	submit: () => void | Promise<void>
	isSaving: boolean
	saveError?: string | null
}

/**
 * A row cannot be created without the three things that make it a row: a name to
 * find it by, the copy itself, and the language that copy is written in — the
 * locale every later translation of it derives from.
 */
const canSubmit = (draft: MessageFormState): boolean =>
	draft.title.trim().length > 0 && draft.content.trim().length > 0 && draft.languageIso.length > 0

/**
 * The draft for a new matrix row: one new piece of copy, in one source locale,
 * classified as it is written. Its type and status are dropdowns rather than
 * defaults because both are what the grid filters on — a row created outside the
 * current filters would be added and then immediately vanish.
 *
 * Only the source row is created here. Its other locales are the grid's own job:
 * tick them for AI translation, or type them in through the row's Edit action.
 * Presentational — `useMatrixAddRow` owns the state and the writes.
 */
export function MatrixAddRow({ add }: { add: MatrixAddRowController }) {
	const { draft, set } = add
	const submit = (e: FormEvent) => {
		e.preventDefault()
		void add.submit()
	}

	return (
		<CardShell as="form" onSubmit={submit} className="mt-4 bg-ds-surface p-4">
			<p className="mb-3 font-medium text-ds-text">New message</p>
			<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
				<FieldShell label="Title" htmlFor="new-row-title" span={3}>
					<Input
						id="new-row-title"
						value={draft.title}
						onChange={(e) => set('title', e.target.value)}
						placeholder="e.g. Carousel card 1 — headline"
					/>
				</FieldShell>

				<EnumField
					label="Type"
					value={draft.messageType}
					labels={MESSAGE_TYPE_LABEL}
					onChange={(v) => set('messageType', v)}
				/>

				<EnumField
					label="Status"
					value={draft.status}
					labels={MESSAGE_STATUS_LABEL}
					onChange={(v) => set('status', v)}
				/>

				<FieldShell label="Source locale">
					<Combobox
						aria-label="Source locale"
						options={add.localeOptions}
						value={draft.languageIso || null}
						onChange={(v) => set('languageIso', v ?? '')}
						placeholder="Select locale…"
					/>
				</FieldShell>

				<FieldShell label="Copy" htmlFor="new-row-content" span={3}>
					<Textarea
						id="new-row-content"
						value={draft.content}
						onChange={(e) => set('content', e.target.value)}
						placeholder="The copy text, in the source locale"
						rows={3}
					/>
				</FieldShell>

				<FieldShell label="Classification" span={3}>
					<ClassifyPicker
						value={draft.classification}
						axes={add.axes}
						onChange={(next) => set('classification', next)}
						emptyLabel="No axes attached — add one to classify this message."
					/>
				</FieldShell>
			</div>

			{add.saveError ? (
				<p role="alert" className="mt-3 text-ds-sm text-ds-danger">
					{add.saveError}
				</p>
			) : null}

			<div className="mt-4 flex items-center gap-2">
				<Button type="submit" size="sm" className="rounded-ds-full" isDisabled={!canSubmit(draft) || add.isSaving}>
					{add.isSaving ? 'Adding…' : 'Create message'}
				</Button>
				<Button type="button" variant="secondary" size="sm" className="rounded-ds-full" onPress={add.cancel}>
					Cancel
				</Button>
			</div>
		</CardShell>
	)
}
